import React, { useState } from 'react';
import {
  AppBar,
  Toolbar,
  Typography,
  Button,
  Container,
  Box,
  CardContent,
  Paper,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  useMediaQuery,
  useTheme,
  IconButton,
  Drawer,
  ListItemButton,
} from '@mui/material';
import MenuIcon from '@mui/icons-material/Menu';
import { Store } from '@mui/icons-material';
import {
  CheckCircle,
  LocalOffer,
  People,
  TrendingUp,
  Security,
  Notifications,
  LocationOn,
  Star,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { AnimatedCard, AnimatedButton, AnimatedBox, BlobCursor, PrismBackground, WaveAnimation, Particles } from '../ui';

const FeaturesPage: React.FC = () => {
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [mobileOpen, setMobileOpen] = useState(false);

  const customerFeatures = [
    {
      icon: <LocalOffer sx={{ fontSize: 40, color: 'primary.main' }} />,
      title: 'Exclusive Deals',
      description: 'Access to special discounts and offers not available elsewhere.',
    },
    {
      icon: <LocationOn sx={{ fontSize: 40, color: 'primary.main' }} />,
      title: 'Location-Based',
      description: 'Find deals near you with our smart location detection.',
    },
    {
      icon: <Notifications sx={{ fontSize: 40, color: 'primary.main' }} />,
      title: 'Push Notifications',
      description: 'Get instant alerts about new deals and limited-time offers.',
    },
    {
      icon: <Star sx={{ fontSize: 40, color: 'primary.main' }} />,
      title: 'Favorites',
      description: 'Save your favorite vendors and deals for easy access.',
    },
  ];

  const vendorFeatures = [
    {
      icon: <Store sx={{ fontSize: 40, color: 'secondary.main' }} />,
      title: 'Business Dashboard',
      description: 'Manage your deals, track performance, and engage with customers.',
    },
    {
      icon: <TrendingUp sx={{ fontSize: 40, color: 'secondary.main' }} />,
      title: 'Analytics',
      description: 'Detailed insights into your deal performance and customer engagement.',
    },
    {
      icon: <People sx={{ fontSize: 40, color: 'secondary.main' }} />,
      title: 'Customer Reach',
      description: 'Connect with thousands of potential customers in your area.',
    },
    {
      icon: <Security sx={{ fontSize: 40, color: 'secondary.main' }} />,
      title: 'Verified Status',
      description: 'Build trust with customers through our verification process.',
    },
  ];

  const benefits = [
    'Save money on everyday purchases',
    'Discover new local businesses',
    'Support your community',
    'Get exclusive member-only deals',
    'Easy-to-use mobile interface',
    'Real-time deal updates',
    'Secure payment options',
    '24/7 customer support',
  ];

  return (
    <Box sx={{ width: '100%', overflowX: 'hidden' }}>
      {/* Header */}
      <AppBar 
        position="sticky" 
        elevation={0}
        sx={{
          backgroundColor: 'white',
          color: '#050206',
          borderBottom: '1px solid rgba(0, 0, 0, 0.08)',
        }}
      >
        <Toolbar
          sx={{
            px: { xs: 2, sm: 4, md: 6 },
            py: 1.5,
          }}
        >
          {/* Logo */}
          <Box sx={{ display: 'flex', alignItems: 'center', flexGrow: { xs: 1, md: 0 } }}>
            <Box
              component="img"
              src="/logo.png"
              alt="DiscountDays Logo"
              onClick={() => navigate('/')}
              sx={{
                width: 40,
                height: 40,
                objectFit: 'contain',
                mr: 1.5,
                cursor: 'pointer',
              }}
            />
            <Typography 
              variant="h6" 
              component="div" 
              onClick={() => navigate('/')}
              sx={{ 
                fontWeight: 700,
                color: '#0084FF',
                fontSize: { xs: '1.1rem', sm: '1.25rem' },
                cursor: 'pointer',
              }}
            >
              DiscountDays
            </Typography>
          </Box>

          {/* Desktop Navigation */}
          {!isMobile && (
            <>
              <Box sx={{ flexGrow: 1, display: 'flex', justifyContent: 'center', gap: 3, mx: 4 }}>
                <Button 
                  onClick={() => navigate('/features')} 
                  sx={{ 
                    color: '#050206',
                    textTransform: 'none',
                    fontWeight: 500,
                    fontSize: '0.95rem',
                    '&:hover': {
                      backgroundColor: 'rgba(59, 59, 59, 0.05)',
                    },
                  }}
                >
                  Features
                </Button>
                <Button 
                  onClick={() => navigate('/about')} 
                  sx={{ 
                    color: '#050206',
                    textTransform: 'none',
                    fontWeight: 500,
                    fontSize: '0.95rem',
                    '&:hover': {
                      backgroundColor: 'rgba(59, 59, 59, 0.05)',
                    },
                  }}
                >
                  About Us
                </Button>
                <Button 
                  onClick={() => navigate('/contact')} 
                  sx={{ 
                    color: '#050206',
                    textTransform: 'none',
                    fontWeight: 500,
                    fontSize: '0.95rem',
                    '&:hover': {
                      backgroundColor: 'rgba(59, 59, 59, 0.05)',
                    },
                  }}
                >
                  Contact
                </Button>
              </Box>

              <Box sx={{ display: 'flex', gap: 1.5 }}>
                <Button
                variant="contained" 
                sx={{ 
                    backgroundColor: '#0084FF',
                    color: 'white',
                    textTransform: 'none',
                    fontWeight: 600,
                    px: 2,
                  '&:hover': {
                      backgroundColor: '#0066CC',
                  },
                }}
                onClick={() => navigate('/login')}
              >
                Admin Login
                </Button>
              </Box>
            </>
          )}

          {/* Mobile Menu Button */}
          {isMobile && (
            <IconButton
              edge="end"
              color="inherit"
              onClick={() => setMobileOpen(true)}
              sx={{ ml: 'auto' }}
            >
              <MenuIcon />
            </IconButton>
          )}
        </Toolbar>

        {/* Mobile Drawer */}
        <Drawer
          anchor="right"
          open={mobileOpen}
          onClose={() => setMobileOpen(false)}
          PaperProps={{
            sx: {
              width: 280,
              backgroundColor: 'white',
            },
          }}
        >
          <Box sx={{ p: 3 }}>
            <Typography variant="h6" sx={{ mb: 3, fontWeight: 700, color: '#3B3B3B' }}>
              Menu
            </Typography>
            <List>
              <ListItem disablePadding>
                <ListItemButton onClick={() => { navigate('/features'); setMobileOpen(false); }}>
                  <ListItemText primary="Features" />
                </ListItemButton>
              </ListItem>
              <ListItem disablePadding>
                <ListItemButton onClick={() => { navigate('/about'); setMobileOpen(false); }}>
                  <ListItemText primary="About Us" />
                </ListItemButton>
              </ListItem>
              <ListItem disablePadding>
                <ListItemButton onClick={() => { navigate('/contact'); setMobileOpen(false); }}>
                  <ListItemText primary="Contact" />
                </ListItemButton>
              </ListItem>
              <ListItem disablePadding sx={{ mt: 2 }}>
                <Button
                  fullWidth
                  variant="contained"
                  sx={{
                    backgroundColor: '#0084FF',
                    color: 'white',
                    textTransform: 'none',
                    fontWeight: 600,
                  }}
                  onClick={() => { navigate('/login'); setMobileOpen(false); }}
                >
                  Admin Login
                </Button>
              </ListItem>
            </List>
          </Box>
        </Drawer>
      </AppBar>

      {/* Hero Section with Blue Gradient Background */}
      <Box
        sx={{
          position: 'relative',
          background: 'linear-gradient(135deg, #0084FF 0%, #00B4FF 100%)',
          py: { xs: 8, md: 12 },
          px: { xs: 2, sm: 4 },
          overflow: 'hidden',
        }}
      >
        <Box
          sx={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            width: '100%',
            height: '100%',
            zIndex: 0,
          }}
        >
          <Particles
            particleColors={['#ffffff', '#ffffff']}
            particleCount={200}
            particleSpread={10}
            speed={0.1}
            particleBaseSize={100}
            moveParticlesOnHover={true}
            alphaParticles={false}
            disableRotation={false}
          />
        </Box>
        
        <Container maxWidth="lg" sx={{ position: 'relative', zIndex: 2 }}>
          <Box
            sx={{
              position: 'relative',
              zIndex: 2,
              color: 'white',
              textAlign: 'center',
            }}
          >
            <Typography 
              variant={isMobile ? 'h3' : 'h2'} 
              component="h1" 
              sx={{
                fontWeight: 700,
                mb: 2,
                fontSize: { xs: '2rem', md: '3rem' },
                animation: 'fadeInUp 0.8s ease-out',
                '@keyframes fadeInUp': {
                  '0%': { opacity: 0, transform: 'translateY(30px)' },
                  '100%': { opacity: 1, transform: 'translateY(0)' },
                },
              }}
            >
              Features & Benefits
            </Typography>
            <Typography 
              variant={isMobile ? 'body1' : 'h6'} 
              sx={{ 
                opacity: 0.95,
                fontSize: { xs: '1rem', md: '1.25rem' },
                animation: 'fadeInUp 0.8s ease-out 0.2s both',
              }}
            >
              Everything you need to save money and grow your business
            </Typography>
          </Box>
        </Container>
      </Box>

      {/* Customer Features */}
      <Container maxWidth="lg" sx={{ py: { xs: 4, md: 8 }, px: { xs: 2, sm: 3 } }}>
        <AnimatedBox delay={0}>
          <Typography variant="h4" component="h2" textAlign="center" gutterBottom>
            For Customers
          </Typography>
        </AnimatedBox>
        <AnimatedBox delay={100}>
          <Typography variant="h6" textAlign="center" color="text.secondary" sx={{ mb: 6 }}>
            Discover amazing deals and save money every day
          </Typography>
        </AnimatedBox>
        
        <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, flexWrap: 'wrap', gap: { xs: 3, md: 4 } }}>
          {customerFeatures.map((feature, index) => (
            <Box sx={{ flex: { xs: '1 1 100%', sm: '1 1 200px' }, minWidth: { xs: '100%', sm: '200px' }, width: '100%' }} key={index}>
              <AnimatedCard delay={index * 100} sx={{ height: '100%', textAlign: 'center', p: 2 }}>
                <CardContent>
                  <Box sx={{ mb: 2, transition: 'transform 0.3s ease', '&:hover': { transform: 'scale(1.1)' } }}>
                    {feature.icon}
                  </Box>
                  <Typography variant="h6" component="h3" gutterBottom>
                    {feature.title}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {feature.description}
                  </Typography>
                </CardContent>
              </AnimatedCard>
            </Box>
          ))}
        </Box>
      </Container>

      {/* Vendor Features */}
      <Box sx={{ backgroundColor: 'white', py: { xs: 4, md: 8 } }}>
        <Container maxWidth="lg" sx={{ px: { xs: 2, sm: 3 } }}>
          <AnimatedBox delay={0}>
            <Typography variant="h4" component="h2" textAlign="center" gutterBottom>
              For Vendors
            </Typography>
          </AnimatedBox>
          <AnimatedBox delay={100}>
            <Typography variant="h6" textAlign="center" color="text.secondary" sx={{ mb: 6 }}>
              Grow your business and reach more customers
            </Typography>
          </AnimatedBox>
          
          <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, flexWrap: 'wrap', gap: { xs: 3, md: 4 } }}>
            {vendorFeatures.map((feature, index) => (
              <Box sx={{ flex: { xs: '1 1 100%', sm: '1 1 200px' }, minWidth: { xs: '100%', sm: '200px' }, width: '100%' }} key={index}>
                <AnimatedCard delay={index * 100} sx={{ height: '100%', textAlign: 'center', p: 2 }}>
                  <CardContent>
                    <Box sx={{ mb: 2, transition: 'transform 0.3s ease', '&:hover': { transform: 'scale(1.1)' } }}>
                      {feature.icon}
                    </Box>
                    <Typography variant="h6" component="h3" gutterBottom>
                      {feature.title}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {feature.description}
                    </Typography>
                  </CardContent>
                </AnimatedCard>
              </Box>
            ))}
          </Box>
        </Container>
      </Box>

      {/* Benefits Section */}
      <Container maxWidth="lg" sx={{ py: { xs: 4, md: 8 }, px: { xs: 2, sm: 3 } }}>
        <Box sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' }, flexWrap: 'wrap', gap: { xs: 4, md: 6 }, alignItems: 'center' }}>
          <AnimatedBox delay={0} sx={{ flex: { xs: '1 1 100%', md: '1 1 400px' }, minWidth: { xs: '100%', md: '400px' }, width: '100%' }}>
            <Typography variant="h4" component="h2" gutterBottom>
              Why Choose DiscountDays?
            </Typography>
            <Typography variant="h6" color="text.secondary" paragraph>
              Join thousands of satisfied customers and vendors who trust our platform.
            </Typography>
            <List>
              {benefits.map((benefit, index) => (
                <ListItem key={index}>
                  <ListItemIcon>
                    <CheckCircle color="primary" />
                  </ListItemIcon>
                  <ListItemText primary={benefit} />
                </ListItem>
              ))}
            </List>
          </AnimatedBox>
          <AnimatedBox delay={200} sx={{ flex: { xs: '1 1 100%', md: '1 1 400px' }, minWidth: { xs: '100%', md: '400px' }, width: '100%' }}>
            <Paper
              sx={{
                p: 4,
                background: 'linear-gradient(135deg, #0084FF 0%, #00B4FF 100%)',
                color: 'white',
                textAlign: 'center',
                transition: 'transform 0.3s ease',
                '&:hover': {
                  transform: 'scale(1.05)',
                },
              }}
            >
              <Typography variant="h3" gutterBottom>
                50K+
              </Typography>
              <Typography variant="h6" gutterBottom>
                Deals Redeemed
              </Typography>
              <Typography variant="body2" sx={{ opacity: 0.9 }}>
                Join our growing community of deal hunters
              </Typography>
            </Paper>
          </AnimatedBox>
        </Box>
      </Container>

      {/* How It Works */}
      <Box sx={{ backgroundColor: 'white', py: { xs: 4, md: 8 } }}>
        <Container maxWidth="lg" sx={{ px: { xs: 2, sm: 3 } }}>
          <AnimatedBox delay={0}>
            <Typography variant="h4" component="h2" textAlign="center" gutterBottom>
              How It Works
            </Typography>
          </AnimatedBox>
          <Box sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' }, flexWrap: 'wrap', gap: { xs: 3, md: 4 }, mt: 4 }}>
            <Box sx={{ flex: { xs: '1 1 100%', md: '1 1 300px' }, minWidth: { xs: '100%', md: '300px' }, width: '100%' }}>
              <AnimatedCard delay={0} sx={{ textAlign: 'center', p: { xs: 2, md: 3 } }}>
                <CardContent>
                  <Typography variant={isMobile ? 'h3' : 'h2'} color="primary.main" gutterBottom>
                    1
                  </Typography>
                  <Typography variant={isMobile ? 'body1' : 'h6'} gutterBottom>
                    Download & Sign Up
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Download our app and create your account in minutes.
                  </Typography>
                </CardContent>
              </AnimatedCard>
            </Box>
            <Box sx={{ flex: { xs: '1 1 100%', md: '1 1 300px' }, minWidth: { xs: '100%', md: '300px' }, width: '100%' }}>
              <AnimatedCard delay={150} sx={{ textAlign: 'center', p: { xs: 2, md: 3 } }}>
                <CardContent>
                  <Typography variant={isMobile ? 'h3' : 'h2'} color="primary.main" gutterBottom>
                    2
                  </Typography>
                  <Typography variant={isMobile ? 'body1' : 'h6'} gutterBottom>
                    Browse Deals
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Discover amazing deals from local businesses near you.
                  </Typography>
                </CardContent>
              </AnimatedCard>
            </Box>
            <Box sx={{ flex: { xs: '1 1 100%', md: '1 1 300px' }, minWidth: { xs: '100%', md: '300px' }, width: '100%' }}>
              <AnimatedCard delay={300} sx={{ textAlign: 'center', p: { xs: 2, md: 3 } }}>
                <CardContent>
                  <Typography variant={isMobile ? 'h3' : 'h2'} color="primary.main" gutterBottom>
                    3
                  </Typography>
                  <Typography variant={isMobile ? 'body1' : 'h6'} gutterBottom>
                    Save & Enjoy
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Redeem your deals and enjoy great savings!
                  </Typography>
                </CardContent>
              </AnimatedCard>
            </Box>
          </Box>
        </Container>
      </Box>

      {/* CTA Section */}
      <Container maxWidth="lg" sx={{ py: { xs: 4, md: 8 }, px: { xs: 2, sm: 3 } }}>
        <AnimatedBox delay={0}>
          <Paper
            sx={{
              p: 6,
              textAlign: 'center',
              background: 'linear-gradient(135deg, #0084FF 0%, #00B4FF 100%)',
              color: 'white',
            }}
          >
            <Typography variant="h4" component="h2" gutterBottom>
              Ready to Start Saving?
            </Typography>
            <Typography variant="h6" sx={{ mb: 4, opacity: 0.9 }}>
              Download our app today and discover amazing deals in your area
            </Typography>
            <AnimatedButton
              variant="contained"
              size="large"
              sx={{
                backgroundColor: 'white',
                color: 'primary.main',
                px: 4,
                py: 1.5,
                '&:hover': {
                  backgroundColor: 'grey.100',
                },
              }}
            >
              Download App
            </AnimatedButton>
          </Paper>
        </AnimatedBox>
      </Container>

      {/* Footer */}
      <Box sx={{ backgroundColor: 'white', borderTop: '1px solid #eee', color: '#050206', py: { xs: 6, md: 8 }, px: { xs: 2, sm: 4 } }}>
        <Container maxWidth="lg">
          <Box sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' }, justifyContent: 'space-between', gap: { xs: 4, md: 6 }, mb: 4 }}>
            <Box sx={{ flex: { xs: '1 1 100%', md: '1 1 40%' } }}>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <Box 
                  component="img"
                  src="/logo.png"
                  alt="DiscountDays Logo"
                  sx={{ width: 40, height: 40, objectFit: 'contain', mr: 1.5 }}
                />
                <Typography variant="h6" sx={{ fontWeight: 700, color: '#0084FF' }}>DiscountDays</Typography>
              </Box>
              <Typography variant="body2" sx={{ color: 'rgba(0, 0, 0, 0.6)' }}>© copyright DiscountDays 2025. All rights reserved.</Typography>
            </Box>

            <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, gap: { xs: 3, sm: 6 }, flex: { xs: '1 1 100%', md: '1 1 60%' } }}>
              <Box>
                <Typography variant="body2" sx={{ fontWeight: 600, mb: 1.5, color: '#0084FF' }}>Socials</Typography>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                  <Typography variant="body2" sx={{ color: 'rgba(0, 0, 0, 0.6)', cursor: 'pointer', '&:hover': { color: '#0084FF' } }}>Instagram</Typography>
                  <Typography variant="body2" sx={{ color: 'rgba(0, 0, 0, 0.6)', cursor: 'pointer', '&:hover': { color: '#0084FF' } }}>LinkedIn</Typography>
                </Box>
              </Box>

              <Box>
                <Typography variant="body2" sx={{ fontWeight: 600, mb: 1.5, color: '#0084FF' }}>Legal</Typography>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                  <Typography variant="body2" onClick={() => navigate('/privacy-policy')} sx={{ color: 'rgba(0, 0, 0, 0.6)', cursor: 'pointer', '&:hover': { color: '#0084FF' } }}>Privacy Policy</Typography>
                  <Typography variant="body2" onClick={() => navigate('/delete-account')} sx={{ color: 'rgba(0, 0, 0, 0.6)', cursor: 'pointer', '&:hover': { color: '#0084FF' } }}>Delete Account</Typography>
                </Box>
              </Box>

              <Box>
                <Typography variant="body2" sx={{ fontWeight: 600, mb: 1.5, color: '#0084FF' }}>Become a Partner</Typography>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                  <Typography variant="body2" sx={{ color: 'rgba(0, 0, 0, 0.6)', cursor: 'pointer', '&:hover': { color: '#0084FF' } }}>Sign Up</Typography>
                  <Typography variant="body2" onClick={() => navigate('/login')} sx={{ color: 'rgba(0, 0, 0, 0.6)', cursor: 'pointer', '&:hover': { color: '#0084FF' } }}>Login</Typography>
                </Box>
              </Box>
            </Box>
          </Box>
        </Container>
      </Box>
    </Box>
  );
};

export default FeaturesPage;
